import React, { useState, useRef } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'

import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { SubTitle, Title } from '@/modules/base/components/texts/index'
import UploadSvg from '@assets/images/icons/picture_upload.svg'
import lang from '@/lang'
import { USER_RIGHTS, ProductTypes } from '@/modules/base/util/constants'
import { checkAccessRights } from '@/modules'
import { ERROR_REASONS } from '@/modules/file/uploader'
import { toast } from '@/modules/base/components/toast/toastConfig'
import StartTrialModal from '@/modules/moduleSubscription/components/start-trial-modal'
import UploadyWrapper from '@/modules/base/components/uploady-wrapper/uploady-wrapper'
import DropZoneUploader from '@/modules/base/components/file-uploader/drop-zone-uploader/drop-zone-uploader'
import { useUpgradeModal } from '@/hooks/useUpgradeModal'

const useStyles = makeStyles(theme => ({
  uploader: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: 190,
    padding: '22px 0px',
  },
  description: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#747474',
    zIndex: 2,
    pointerEvents: 'none',
    textAlign: 'center',
  },
  special: {
    color: theme.primary,
    fontWeight: 500,
  },
  fileContainerButton: {
    pointerEvents: 'auto',
  },
  iconWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: '#fff',
    height: '200px',
    width: '200px',
    borderRadius: '50%',
    border: `2px dashed ${theme.primaryLight}`,
    marginBottom: 48,
    marginRight: 10,
  },
  primary: {
    color: theme.primary,
  },
  uploadyWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dropzoneUploader: {
    height: 190,
  },
}))

const FileUploadChooser = ({
  uploader,
  title = lang.fileUploadChooser.title,
  uploadLabel = lang.fileUploadChooser.uploadLabel,
  fileContainerLabel = lang.fileUploadChooser.fileContainerLabel,
  onOpenFileContainer,
  multiple = true,
  accept = [],
  className = null,
  showIcon = false,
  children = null,
  useDmsUpload = false,
}) => {
  const classes = useStyles()
  const openUpgradeModal = useUpgradeModal()
  const [openTrialModal, setOpenTrialModal] = useState(false)
  const subscriptions = useSelector(state => state.subscription.details.subscriptions)
  const userInfo = useSelector(state => state.user.info)
  const uploadTriggerRef = useRef(null)

  const documentSub = subscriptions.find(sub => sub.typeOfSubscription === ProductTypes.DOCUMENT)
  const accessRights = { read: checkAccessRights(USER_RIGHTS.DOCUMENTS.READ, userInfo), write: checkAccessRights(USER_RIGHTS.DOCUMENTS.WRITE, userInfo) }
  const hasAccess = accessRights?.write || accessRights?.read

  const onOpenContainer = (event) => {
    if (!documentSub && !hasAccess) {
      return setOpenTrialModal(true)
    }
    if (!hasAccess && documentSub?.isExpired) {
      return openUpgradeModal({ subscriptionType: ProductTypes.DOCUMENT })
    }
    onOpenFileContainer?.(event)
  }

  const onDropError = (error) => {
    const message = error === ERROR_REASONS.TOO_MANY_FILES_ERROR
      ? lang.fileUploaderNoMultipleUploadError2
      : lang.defaultErrorMessage
    toast.showToast({ message, type: 'error' })
  }

  const handleFiles = (files) => {
    if (uploader?.onFilesAdded) {
      uploader.onFilesAdded(files)
    }
  }

  const titleClass = clsx('title', (!userInfo.isOwner && !hasAccess) && classes.primary)

  const fileContainerControl = onOpenFileContainer && (
    <>
      {' oder '}
      <span
        className={clsx('special', classes.special, classes.fileContainerButton)}
        onClick={onOpenContainer}>
        {fileContainerLabel}
      </span>
    </>
  )

  const uploaderContent = (
    <DropZoneUploader className={classes.dropzoneUploader} uploader={uploader} multiple={multiple} accept={accept} onDropError={onDropError}>
      <div className={clsx('uploader', classes.uploader, className)}>
        {showIcon && (
          <div className={clsx('iconWrapper', classes.iconWrapper)}>
            <SVGIcon svg={UploadSvg} customClass={classes.icon} height='80px' width='80px' />
          </div>
        )}
        <div className={clsx('description', classes.description)}>
          <Title className={titleClass}>{title}</Title>
          {(userInfo.isOwner || hasAccess) && (
            <SubTitle className='subTitle'>
              <span className={clsx('special', classes.special)}>{uploadLabel}</span>
              {/* eslint-disable-next-line react/jsx-no-undef */}
              {fileContainerControl}
            </SubTitle>
          )}
          {children}
        </div>
      </div>
    </DropZoneUploader>
  )

  const uploadyContent = (
    <UploadyWrapper
      multiple={multiple}
      allowedExtensions={accept}
      onSubmit={handleFiles}
      uploadTriggerRef={uploadTriggerRef}
      className={classes.uploadyWrapper}
    >
      <div className={clsx('uploader', classes.uploader, className)}>
        {uploaderContent}
      </div>
    </UploadyWrapper>
  )

  const uploaderContentProps = {
    classes,
    showIcon,
    title,
    titleClass,
    userInfo,
    hasAccess,
    uploadLabel,
    fileContainerControl,
    className,
  }

  if (!uploader) {
    uploaderContentProps.className = className
  }

  return (
    <>
      {useDmsUpload ? uploadyContent : uploaderContent}
      <StartTrialModal
        open={openTrialModal}
        productType={ProductTypes.DOCUMENT}
        onClose={() => setOpenTrialModal(false)}
      />
    </>
  )
}

FileUploadChooser.propTypes = {
  uploader: PropTypes.object,
  title: PropTypes.string,
  uploadLabel: PropTypes.string,
  fileContainerLabel: PropTypes.string,
  onOpenFileContainer: PropTypes.func,
  multiple: PropTypes.bool,
  showIcon: PropTypes.bool,
  accept: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  useDmsUpload: PropTypes.bool,
}

export default FileUploadChooser
